export type ReviewStatus = "published" | "coming-soon";

export type BlogPost = {
  title: string;
  description: string;
  slug: string;
  datePublished: string;
  dateModified: string;
  author: string;
  heroImage: string;
  status: ReviewStatus;
  body: string;
};

const REQUIRED_FIELDS = [
  "title",
  "description",
  "slug",
  "datePublished",
  "dateModified",
  "author",
  "heroImage",
] as const;

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

function assertIsoDate(value: string, field: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    throw new Error(`${field} must be YYYY-MM-DD`);
  }
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) {
    throw new Error(`${field} is not a real calendar date`);
  }
  return value;
}

function filenameStem(sourcePath: string): string {
  const base = sourcePath.split(/[/\\]/).pop() ?? sourcePath;
  return base.replace(/\.md$/i, "");
}

function parseFrontmatter(block: string): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const line of block.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith("#")) {
      continue;
    }
    const match = trimmed.match(/^([A-Za-z][A-Za-z0-9]*):\s*(.*)$/);
    if (!match) {
      throw new Error(`Invalid blog frontmatter line: ${line}`);
    }
    let value = match[2].trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    fields[match[1]] = value;
  }
  return fields;
}

/** Parse one Markdown article. The filename stem and `slug` must match. */
export function parseBlogSource(sourcePath: string, raw: string): BlogPost {
  const match = raw.match(FRONTMATTER);
  if (!match) {
    throw new Error(`${sourcePath} is missing YAML frontmatter`);
  }
  const fields = parseFrontmatter(match[1]);
  for (const field of REQUIRED_FIELDS) {
    if (!fields[field]?.trim()) {
      throw new Error(`${sourcePath} is missing ${field}`);
    }
  }
  const slug = fields.slug.trim();
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug)) {
    throw new Error(`${sourcePath} has an invalid slug`);
  }
  const stem = filenameStem(sourcePath);
  if (stem !== slug) {
    throw new Error(`Slug "${slug}" does not match filename "${stem}.md"`);
  }
  const heroImage = fields.heroImage.trim();
  if (!heroImage.startsWith("/") && !/^https?:\/\//i.test(heroImage)) {
    throw new Error(`${sourcePath} heroImage must be a site path or absolute URL`);
  }
  const status = (fields.status?.trim() || "published") as ReviewStatus;
  if (status !== "published" && status !== "coming-soon") {
    throw new Error(`${sourcePath} status must be published or coming-soon`);
  }
  return {
    title: fields.title.trim(),
    description: fields.description.trim(),
    slug,
    datePublished: assertIsoDate(fields.datePublished.trim(), "datePublished"),
    dateModified: assertIsoDate(fields.dateModified.trim(), "dateModified"),
    author: fields.author.trim(),
    heroImage,
    status,
    body: raw.slice(match[0].length).trim(),
  };
}

/** The latest review that is already published and dated before this one. */
export function previousPublishedReview(post: BlogPost, posts: BlogPost[]): BlogPost | undefined {
  return posts
    .filter((item) => item.status !== "coming-soon" && item.datePublished < post.datePublished)
    .sort((a, b) => (a.datePublished < b.datePublished ? 1 : -1))[0];
}

/** Newest published date first. Slug breaks ties so the order stays stable. */
export function sortBlogPosts(posts: BlogPost[]): BlogPost[] {
  return [...posts].sort((a, b) => {
    if (a.datePublished !== b.datePublished) {
      return a.datePublished < b.datePublished ? 1 : -1;
    }
    return a.slug.localeCompare(b.slug);
  });
}
